import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { Row, Col, Card, Table, Badge, Form, Button } from "react-bootstrap";

import landmarkImg from "../../../../images/landmark/logo-text-dark.png";
import ApiService from "../../../../services/ApiService";
import PageHeaderBar from "../../../layouts/PageHeaderBar";
import { useDocumentTitle } from "../../../utils";
import { addCommas, epochToDateTimeString, timeAgo } from "../../../../utils";
import { ThemeContext } from "../../../../context/ThemeContext";
import { useAuth } from "../../../Providers/LandmarkAuthContext";
import EmployeeInfo from "./EmployeeInfo";

const PAGE_SIZE = 100;
const AUTO_REFRESH_MS = 20000;

const STATUS_META = {
  pending: { label: "Pending", variant: "secondary" },
  processing: { label: "Processing", variant: "info" },
  done: { label: "Done", variant: "success" },
  no_match: { label: "No match", variant: "warning" },
  error: { label: "Error", variant: "danger" },
};

const SOURCE_LABELS = {
  "campaign-upload": "Campaign Uploader",
  sendora: "Sendora",
  millionverifier: "MillionVerifier",
};

function sourceLabel(source) {
  if (!source) return "(unknown)";
  return SOURCE_LABELS[source] || source;
}

function statusBadge(status) {
  const meta = STATUS_META[status] || { label: status || "—", variant: "light" };
  return <Badge bg={meta.variant}>{meta.label}</Badge>;
}

function buildContactUrl(baseUrl, contactId) {
  if (!baseUrl || !contactId) return "";
  const trimmed = String(baseUrl).replace(/\/+$/, "");
  return `${trimmed}/lightning/r/Contact/${contactId}/view`;
}

const SummaryStat = ({ label, value, variant = "dark" }) => (
  <Col xs={6} md={3} lg={2} className="mb-2">
    <Card className="h-100 border-0 shadow-sm">
      <Card.Body className="py-2 px-3">
        <div className={`fs-5 fw-bold text-${variant}`}>{value}</div>
        <div className="text-muted small text-uppercase">{label}</div>
      </Card.Body>
    </Card>
  </Col>
);

const EmailBounceForm = ({ employee }) => {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const searchTimer = useRef(null);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const api = new ApiService({ employee });
        const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
        if (statusFilter) params.status = statusFilter;
        if (sourceFilter) params.source = sourceFilter;
        if (search.trim()) params.search = search.trim();
        const res = await api.get(
          "landmark/salesforce/emailBounceCleanup/results",
          params
        );
        if (!res || res.ok !== true) {
          if (!silent) toast.error(res?.error || "Failed to load results");
          return;
        }
        setSummary(res.summary || null);
        setBaseUrl(res.salesforceBaseUrl || "");
        setRows(Array.isArray(res.rows) ? res.rows : []);
        setTotal(Number(res.total || 0));
      } catch (err) {
        if (!silent) toast.error(err?.message || "Failed to load results");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [employee, page, statusFilter, sourceFilter, search]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => load({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const onSearchChange = (value) => {
    setSearch(value);
    setPage(0);
    if (searchTimer.current) clearTimeout(searchTimer.current);
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const api = new ApiService({ employee });
      const res = await api.post(
        "landmark/salesforce/emailBounceCleanup/run",
        {},
        undefined,
        { timeout: 120000 }
      );
      if (!res || res.ok !== true) {
        toast.error(res?.error || "Run failed");
      } else {
        const s = res.summary || {};
        toast.success(
          `Claimed ${s.claimed ?? 0} • done ${s.done ?? 0} • contacts ${
            s.updatedContacts ?? 0
          } • no match ${s.noMatch ?? 0}`
        );
        await load();
      }
    } catch (err) {
      toast.error(err?.message || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const bySourceEntries = Object.entries(summary?.bySource || {});

  return (
    <div className="email-bounce-dashboard">
      <Row className="g-2 mb-2">
        <SummaryStat label="Total" value={addCommas(summary?.total || 0)} />
        <SummaryStat
          label="Contacts updated"
          value={addCommas(summary?.contactsUpdated || 0)}
          variant="success"
        />
        <SummaryStat
          label="Done"
          value={addCommas(summary?.done || 0)}
          variant="success"
        />
        <SummaryStat
          label="Pending"
          value={addCommas(summary?.pending || 0)}
          variant="secondary"
        />
        <SummaryStat
          label="No match"
          value={addCommas(summary?.noMatch || 0)}
          variant="warning"
        />
        <SummaryStat
          label="Errors"
          value={addCommas(summary?.error || 0)}
          variant="danger"
        />
      </Row>

      {bySourceEntries.length > 0 && (
        <div className="mb-3 small text-muted">
          <span className="me-2 fw-semibold">By source:</span>
          {bySourceEntries.map(([src, count]) => (
            <Badge bg="light" text="dark" className="me-2 border" key={src}>
              {sourceLabel(src)}: {addCommas(count)}
            </Badge>
          ))}
        </div>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
            <Form.Group style={{ minWidth: 150 }}>
              <Form.Label className="small mb-1">Status</Form.Label>
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="done">Done</option>
                <option value="no_match">No match</option>
                <option value="error">Error</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ minWidth: 170 }}>
              <Form.Label className="small mb-1">Source</Form.Label>
              <Form.Select
                size="sm"
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">All</option>
                <option value="campaign-upload">Campaign Uploader</option>
                <option value="sendora">Sendora</option>
                <option value="millionverifier">MillionVerifier</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ minWidth: 220, flex: 1 }}>
              <Form.Label className="small mb-1">Search email</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="name@example.com"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </Form.Group>
            <div className="ms-auto d-flex gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => load()}
                disabled={loading}
              >
                {loading ? "Refreshing…" : "Refresh"}
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={runNow}
                disabled={running}
              >
                {running ? "Running…" : "Run cleanup now"}
              </Button>
            </div>
          </div>

          <div className="table-responsive">
            <Table hover size="sm" className="align-middle mb-0">
              <thead>
                <tr className="small text-uppercase text-muted">
                  <th>Email</th>
                  <th>Verdict</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Contacts updated</th>
                  <th>Fields</th>
                  <th className="text-end">Attempts</th>
                  <th>Processed</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      {loading ? "Loading…" : "No records found."}
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="text-break" style={{ maxWidth: 230 }}>
                      {row.email}
                    </td>
                    <td>
                      <Badge bg="light" text="dark" className="border">
                        {row.result || "—"}
                      </Badge>
                    </td>
                    <td className="small">{sourceLabel(row.source)}</td>
                    <td>
                      {statusBadge(row.status)}
                      {row.status === "error" && row.lastError ? (
                        <div
                          className="text-danger small text-truncate"
                          style={{ maxWidth: 200 }}
                          title={row.lastError}
                        >
                          {row.lastError}
                        </div>
                      ) : null}
                    </td>
                    <td className="small">
                      {(() => {
                        const contacts =
                          Array.isArray(row.matchedContacts) &&
                          row.matchedContacts.length
                            ? row.matchedContacts
                            : (row.matchedContactIds || []).map((cid) => ({
                                id: cid,
                                name: "",
                              }));
                        if (!contacts.length) {
                          return <span className="text-muted">—</span>;
                        }
                        return (
                          <div
                            className="d-flex flex-wrap gap-1"
                            style={{ maxWidth: 280 }}
                          >
                            {contacts.map((contact) => {
                              const label = contact.name || contact.id;
                              return baseUrl ? (
                                <a
                                  key={contact.id}
                                  href={buildContactUrl(baseUrl, contact.id)}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={`${contact.name ? contact.name + " — " : ""}${contact.id}`}
                                  className="badge bg-success-subtle text-success border text-decoration-none"
                                >
                                  {label}
                                </a>
                              ) : (
                                <span
                                  key={contact.id}
                                  title={`${contact.name ? contact.name + " — " : ""}${contact.id}`}
                                  className="badge bg-success-subtle text-success border"
                                >
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </td>
                    <td
                      className="small text-muted text-truncate"
                      style={{ maxWidth: 200 }}
                      title={row.matchedFieldSummary}
                    >
                      {row.matchedFieldSummary || "—"}
                    </td>
                    <td className="text-end small">{row.attempts}</td>
                    <td className="small">
                      {row.processedTime ? (
                        <span title={epochToDateTimeString(row.processedTime)}>
                          {timeAgo(row.processedTime)}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="d-flex align-items-center justify-content-between mt-3">
            <div className="small text-muted">
              {addCommas(total)} record{total === 1 ? "" : "s"} • page{" "}
              {page + 1} of {totalPages}
            </div>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page <= 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page >= totalPages - 1 || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

const EmailBounceCleanupDashboard = () => {
  const { changeBackground } = useContext(ThemeContext);
  const { employee, isAuthenticated, loading, logout } = useAuth();

  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !employee) return;
    try {
      const api = new ApiService({ employee });
      api.get("landmark/employees/login");
    } catch (err) {
      console.warn("employee login touch failed", err);
    }
  }, [isAuthenticated, employee]);

  useDocumentTitle("Email Bounce Cleanup - Landmark Ventures");

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <Fragment>
      <div className="global-dash-wrapper">
        <div className="d-flex align-items-center justify-content-center">
          <img
            src={landmarkImg}
            className="img-fluid landmark-img"
            alt="Landmark"
          />
        </div>

        {isAuthenticated && employee && (
          <div className="landmark-sign-in" style={{ marginBottom: "18px" }}>
            <div className="existing-accounts-container">
              <EmployeeInfo
                landmarkEmployee={employee}
                disableHover={true}
                showLogout={true}
                logout={logout}
              />
            </div>
          </div>
        )}

        <PageHeaderBar
          pages={[
            { title: "Landmark", url: `` },
            {
              title: "Email Bounce Cleanup",
              url: `/landmark/emailbounce/dashboard`,
            },
          ]}
        />

        {isAuthenticated ? (
          <EmailBounceForm employee={employee} />
        ) : (
          <div className="text-center text-muted p-5">
            Please sign in to access the dashboard.
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default EmailBounceCleanupDashboard;
